import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { locationsAPI, usersAPI } from '@/lib/api';
import { toast } from 'sonner';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Plus, MoreHorizontal, Pencil, Trash2, Loader2, Globe2, MapPin } from 'lucide-react';

const BV_LABELS = {
  truckwash: 'Truckwash',
  truckstop: 'Truckstop',
  truckshop: 'Truckshop',
  truckssupply: 'Truckssupply',
};
const BV_ORDER = ['truckwash', 'truckstop', 'truckshop', 'truckssupply'];

const STATUS_TONE = {
  actief: 'bg-emerald-50 text-emerald-700 border-emerald-100',
  gepland: 'bg-amber-50 text-amber-700 border-amber-100',
  inactief: 'bg-slate-100 text-slate-500 border-slate-200',
};
const STATUS_LABELS = { actief: 'Actief', gepland: 'Gepland', inactief: 'Inactief' };

export default function LocationsTab() {
  const [locations, setLocations] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const loadAll = useCallback(async () => {
    try {
      const [locRes, userRes] = await Promise.all([locationsAPI.list(), usersAPI.list()]);
      if (locRes.ok) setLocations(await locRes.clone().json());
      if (userRes.ok) setUsers(await userRes.clone().json());
    } catch { /* tolerate */ }
    setLoading(false);
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  // Per-slug user count + global "unlimited access" count
  const { countBySlug, unlimitedCount } = useMemo(() => {
    const counts = {};
    let unlimited = 0;
    for (const u of users) {
      const locs = Array.isArray(u.locations) ? u.locations : [];
      if (locs.length === 0) {
        unlimited += 1;
      } else {
        for (const slug of locs) {
          counts[slug] = (counts[slug] || 0) + 1;
        }
      }
    }
    return { countBySlug: counts, unlimitedCount: unlimited };
  }, [users]);

  // Group locations by bv_id with deterministic order
  const grouped = useMemo(() => {
    const map = {};
    for (const loc of locations) {
      const bv = loc.bv_id || 'overig';
      if (!map[bv]) map[bv] = [];
      map[bv].push(loc);
    }
    Object.values(map).forEach(arr =>
      arr.sort((a, b) => a.display_name.localeCompare(b.display_name, 'nl'))
    );
    return map;
  }, [locations]);

  const orderedBvs = [
    ...BV_ORDER.filter(b => grouped[b]),
    ...Object.keys(grouped).filter(b => !BV_ORDER.includes(b)).sort(),
  ];

  const totalActive = locations.filter(l => l.status === 'actief').length;

  return (
    <div className="space-y-4" data-testid="locations-tab">
      {/* Top action + summary */}
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-[#1a2332]">Locaties</h2>
          <p className="text-xs text-slate-500 mt-0.5">
            Beheer de TW1-locaties die in de gebruikersrechten gebruikt worden.
          </p>
        </div>
        <Button
          onClick={() => setShowAddDialog(true)}
          className="bg-[#1a2332] hover:bg-[#111827] text-white rounded-xl btn-press"
          data-testid="locations-add-button"
        >
          <Plus className="w-4 h-4 mr-1.5" /> Locatie toevoegen
        </Button>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <Card className="border border-slate-200">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-[#1a2332]/5 flex items-center justify-center">
              <MapPin className="w-4 h-4 text-[#1a2332]" />
            </div>
            <div>
              <p className="text-2xl font-semibold text-[#1a2332]">{locations.length}</p>
              <p className="text-[11px] text-slate-500 uppercase tracking-wider">Totaal locaties</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border border-slate-200">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-emerald-50 flex items-center justify-center">
              <MapPin className="w-4 h-4 text-emerald-600" />
            </div>
            <div>
              <p className="text-2xl font-semibold text-[#1a2332]">{totalActive}</p>
              <p className="text-[11px] text-slate-500 uppercase tracking-wider">Actief</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border border-slate-200" data-testid="locations-unlimited-count">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-[#d4a853]/10 flex items-center justify-center">
              <Globe2 className="w-4 h-4 text-[#d4a853]" />
            </div>
            <div>
              <p className="text-2xl font-semibold text-[#1a2332]">{unlimitedCount}</p>
              <p className="text-[11px] text-slate-500 uppercase tracking-wider">
                Onbeperkte toegang
              </p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Grouped tables */}
      {loading ? (
        <Card className="border border-slate-200">
          <CardContent className="p-4 space-y-3">
            {[1, 2, 3, 4].map(i => <Skeleton key={i} className="h-12 w-full" />)}
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {orderedBvs.map(bv => (
            <Card key={bv} className="border border-slate-200" data-testid={`locations-table-${bv}`}>
              <CardContent className="p-0">
                <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
                  <h3 className="text-sm font-semibold text-[#1a2332]">
                    {BV_LABELS[bv] || bv}
                  </h3>
                  <Badge variant="outline" className="text-[10px]">
                    {grouped[bv].length} locatie{grouped[bv].length === 1 ? '' : 's'}
                  </Badge>
                </div>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[40%]">Locatie</TableHead>
                      <TableHead>Slug</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Gebruikers</TableHead>
                      <TableHead className="text-right">Acties</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {grouped[bv].map(loc => (
                      <TableRow key={loc.slug} data-testid={`locations-row-${loc.slug}`}>
                        <TableCell className="font-medium text-sm text-[#1a2332]">
                          {loc.display_name}
                        </TableCell>
                        <TableCell>
                          <code className="text-[11px] font-mono text-slate-500 bg-slate-50 px-1.5 py-0.5 rounded">
                            {loc.slug}
                          </code>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className={`text-[10px] ${STATUS_TONE[loc.status] || ''}`}>
                            {STATUS_LABELS[loc.status] || loc.status}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right text-sm text-slate-600" data-testid={`locations-count-${loc.slug}`}>
                          {countBySlug[loc.slug] || 0}
                        </TableCell>
                        <TableCell className="text-right">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon" className="h-8 w-8" data-testid={`locations-actions-${loc.slug}`}>
                                <MoreHorizontal className="w-4 h-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem
                                onClick={() => setEditTarget(loc)}
                                data-testid={`locations-edit-item-${loc.slug}`}
                              >
                                <Pencil className="w-4 h-4 mr-2" /> Bewerken
                              </DropdownMenuItem>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem
                                onClick={() => setDeleteTarget(loc)}
                                className="text-red-600 focus:text-red-600"
                                data-testid={`locations-delete-item-${loc.slug}`}
                              >
                                <Trash2 className="w-4 h-4 mr-2" /> Verwijderen
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {showAddDialog && (
        <LocationDialog
          mode="create"
          existingBvs={Object.keys(grouped).length ? Object.keys(grouped) : BV_ORDER}
          onClose={() => setShowAddDialog(false)}
          onSuccess={() => { setShowAddDialog(false); loadAll(); }}
        />
      )}

      {editTarget && (
        <LocationDialog
          mode="edit"
          location={editTarget}
          existingBvs={Object.keys(grouped).length ? Object.keys(grouped) : BV_ORDER}
          onClose={() => setEditTarget(null)}
          onSuccess={() => { setEditTarget(null); loadAll(); }}
        />
      )}

      {deleteTarget && (
        <DeleteConfirmDialog
          location={deleteTarget}
          userCount={countBySlug[deleteTarget.slug] || 0}
          onClose={() => setDeleteTarget(null)}
          onSuccess={() => { setDeleteTarget(null); loadAll(); }}
        />
      )}
    </div>
  );
}


function LocationDialog({ mode, location, existingBvs, onClose, onSuccess }) {
  const isEdit = mode === 'edit';
  const [form, setForm] = useState(() => ({
    slug: location?.slug || '',
    display_name: location?.display_name || '',
    bv_id: location?.bv_id || (existingBvs[0] || 'truckwash'),
    status: location?.status || 'actief',
  }));
  const [bvMode, setBvMode] = useState('existing'); // 'existing' | 'custom'
  const [customBv, setCustomBv] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const bvOptions = useMemo(() => {
    const set = new Set([...BV_ORDER, ...existingBvs]);
    return [...set];
  }, [existingBvs]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    const finalBv = bvMode === 'custom' ? customBv.trim().toLowerCase() : form.bv_id;
    if (!finalBv) {
      setError('BV ID mag niet leeg zijn');
      return;
    }
    if (!form.display_name.trim()) {
      setError('Display naam mag niet leeg zijn');
      return;
    }

    setSaving(true);
    let res;
    try {
      if (isEdit) {
        res = await locationsAPI.update(location.slug, {
          display_name: form.display_name.trim(),
          bv_id: finalBv,
          status: form.status,
        });
      } else {
        res = await locationsAPI.create({
          slug: form.slug.trim().toLowerCase(),
          display_name: form.display_name.trim(),
          bv_id: finalBv,
          status: form.status,
        });
      }
    } catch {
      setError('Netwerkfout');
      setSaving(false);
      return;
    }

    if (res.ok) {
      toast.success(isEdit ? 'Locatie bijgewerkt' : 'Locatie aangemaakt');
      onSuccess();
    } else {
      let detail = '';
      try { detail = (await res.clone().json())?.detail || ''; } catch { /* ignore */ }
      setError(typeof detail === 'string' ? detail : `Fout (HTTP ${res.status})`);
    }
    setSaving(false);
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md" data-testid="locations-dialog">
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Locatie bewerken' : 'Locatie toevoegen'}</DialogTitle>
          <DialogDescription className="sr-only">
            {isEdit
              ? 'Wijzig de weergavenaam, BV of status van deze locatie. De slug is niet wijzigbaar.'
              : 'Voer een unieke slug, weergavenaam en BV in om een nieuwe locatie aan te maken.'}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          {error && (
            <p className="text-sm text-red-600 bg-red-50 p-2 rounded" data-testid="locations-dialog-error">
              {error}
            </p>
          )}
          <div className="space-y-2">
            <Label>Slug</Label>
            <Input
              value={form.slug}
              onChange={e => setForm({...form, slug: e.target.value.toLowerCase().replace(/\s+/g, '-')})}
              placeholder="bijv. amsterdam-noord"
              required
              disabled={isEdit}
              className={isEdit ? 'bg-slate-50 text-slate-500 font-mono' : 'font-mono'}
              data-testid="locations-dialog-slug"
            />
            {isEdit && (
              <p className="text-[10px] text-slate-400">Slug is niet wijzigbaar.</p>
            )}
          </div>
          <div className="space-y-2">
            <Label>Display naam</Label>
            <Input
              value={form.display_name}
              onChange={e => setForm({...form, display_name: e.target.value})}
              placeholder="bijv. Amsterdam Noord"
              required
              data-testid="locations-dialog-displayname"
            />
          </div>
          <div className="space-y-2">
            <Label>BV</Label>
            {bvMode === 'existing' ? (
              <Select value={form.bv_id} onValueChange={v => {
                if (v === '__custom__') { setBvMode('custom'); setCustomBv(''); }
                else setForm({...form, bv_id: v});
              }}>
                <SelectTrigger data-testid="locations-dialog-bv"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {bvOptions.map(b => (
                    <SelectItem key={b} value={b}>{BV_LABELS[b] || b}</SelectItem>
                  ))}
                  <SelectItem value="__custom__">+ Nieuwe BV…</SelectItem>
                </SelectContent>
              </Select>
            ) : (
              <div className="flex gap-2">
                <Input
                  value={customBv}
                  onChange={e => setCustomBv(e.target.value.toLowerCase())}
                  placeholder="bijv. truckparts"
                  className="font-mono"
                  data-testid="locations-dialog-bv-custom"
                />
                <Button type="button" variant="outline" size="sm" onClick={() => setBvMode('existing')}>
                  Annuleren
                </Button>
              </div>
            )}
          </div>
          <div className="space-y-2">
            <Label>Status</Label>
            <Select value={form.status} onValueChange={v => setForm({...form, status: v})}>
              <SelectTrigger data-testid="locations-dialog-status"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="actief">Actief</SelectItem>
                <SelectItem value="gepland">Gepland</SelectItem>
                <SelectItem value="inactief">Inactief</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} className="rounded-xl" disabled={saving}>
              Annuleren
            </Button>
            <Button
              type="submit"
              disabled={saving}
              className="bg-[#1a2332] hover:bg-[#111827] text-white rounded-xl"
              data-testid="locations-dialog-submit"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : (isEdit ? 'Opslaan' : 'Toevoegen')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}


function DeleteConfirmDialog({ location, userCount, onClose, onSuccess }) {
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState('');
  const blocked = userCount > 0;

  const handleDelete = async () => {
    setDeleting(true);
    setError('');
    try {
      const res = await locationsAPI.delete(location.slug);
      if (res.ok) {
        toast.success('Locatie verwijderd');
        onSuccess();
        return;
      }
      let detail = '';
      try { detail = (await res.clone().json())?.detail || ''; } catch { /* ignore */ }
      setError(typeof detail === 'string' ? detail : `Fout (HTTP ${res.status})`);
    } catch {
      setError('Netwerkfout');
    }
    setDeleting(false);
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md" data-testid="locations-delete-dialog">
        <DialogHeader>
          <DialogTitle>Locatie verwijderen?</DialogTitle>
          <DialogDescription className="sr-only">
            Bevestig of annuleer het verwijderen van deze locatie. Verwijdering wordt geblokkeerd wanneer er nog gebruikers aan deze locatie gekoppeld zijn.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <p className="text-sm text-slate-600">
            Wil je <strong>{location.display_name}</strong> ({location.slug}) verwijderen?
          </p>
          {blocked && (
            <div className="bg-amber-50 border border-amber-100 text-amber-900 text-xs rounded-lg px-3 py-2">
              {userCount} gebruiker{userCount === 1 ? '' : 's'} {userCount === 1 ? 'heeft' : 'hebben'} deze
              locatie nog. Verwijdering wordt geblokkeerd; pas eerst hun locatie-toewijzing aan.
            </div>
          )}
          {error && (
            <p className="text-sm text-red-600 bg-red-50 p-2 rounded" data-testid="locations-delete-error">
              {error}
            </p>
          )}
        </div>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose} className="rounded-xl" disabled={deleting}>
            Annuleren
          </Button>
          <Button
            type="button"
            onClick={handleDelete}
            disabled={deleting || blocked}
            className="bg-red-600 hover:bg-red-700 text-white rounded-xl"
            data-testid="locations-delete-confirm"
          >
            {deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Verwijderen'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
